import { useQuery } from "@apollo/client";
import { ChatBubbleLeftRightIcon, ClockIcon, SignalIcon, UserIcon, VideoCameraIcon } from "@heroicons/react/24/solid";
import { Button, Collapse, IconButton } from "@mui/material";
import { useState } from "react";
import styled from "styled-components";
import EmptyState from "../../components/EmptyState/EmptyState";
import { ErrorAlert } from "../../components/ErrorAlert/ErrorAlert";
import Loader from "../../components/Loader/Loader";
import Page from "../../components/Page/Page";
import Panel from "../../components/Panel/Panel";
import Badge from "../../components/Styled/Badge";
import { Row } from "../../components/Styled/Container";
import {
  VideoChatHistory as VideoChatHistoryData,
  VideoChatHistorySession,
  VideoChatMessage,
  VideoChatUser,
} from "../../graphql/graphql";
import { VIDEO_CHAT_HISTORY } from "../../graphql/queries-gql";
import { ArrowPathIcon, ChevronDownIcon, ChevronUpIcon, VideoCameraIcon as VideoCameraOutlineIcon } from "@heroicons/react/24/outline";

const SessionItem = styled.div`
  border-bottom: 1px solid ${(props) => props.theme.border};

  &:last-child {
    border-bottom: 0;
  }
`;

const SessionHeader = styled.div<{ $expanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px 24px;
  padding: 12px 18px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  background-color: ${(props) =>
    props.$expanded ? props.theme.surfaceHover : "transparent"};

  &:hover {
    background-color: ${(props) => props.theme.surfaceHover};
  }
`;

const Pair = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 20px;
  min-width: 0;
`;

const PairIcon = styled.span`
  display: inline-flex;
  color: ${(props) => props.theme.colors.green};
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const UserAvatar = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

const AvatarFallback = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${(props) => props.theme.surfaceActive};
  color: ${(props) => props.theme.secondaryTextColor};
`;

const UserText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const UserName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => props.theme.textColor};
`;

const Mono = styled.span`
  font-family: ${(props) => props.theme.monoFontFamily};
  font-size: 11.5px;
  color: ${(props) => props.theme.mutedTextColor};
`;

const HeaderMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const MetaCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

const MetaLabel = styled.span`
  font-size: 12px;
  color: ${(props) => props.theme.secondaryTextColor};
  font-variant-numeric: tabular-nums;
`;

const DurationValue = styled.span`
  font-size: 15px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${(props) => props.theme.colors.green};
`;

const SessionDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  padding: 16px 18px 20px;
  border-top: 1px solid ${(props) => props.theme.border};
`;

const DetailCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => props.theme.textColor};

  svg {
    color: ${(props) => props.theme.secondaryTextColor};
  }
`;

const DetailRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 13px;
  color: ${(props) => props.theme.textColor};
  font-variant-numeric: tabular-nums;

  svg {
    flex-shrink: 0;
    color: ${(props) => props.theme.mutedTextColor};
  }
`;

const DetailLabel = styled.span`
  color: ${(props) => props.theme.secondaryTextColor};
`;

const StatsRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 24px;
  margin-top: 8px;
  padding: 12px;
  border: 1px solid ${(props) => props.theme.border};
  border-radius: 8px;
  background-color: ${(props) => props.theme.surfaceHover};

  > svg {
    color: ${(props) => props.theme.secondaryTextColor};
  }
`;

const Stat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StatLabel = styled.span`
  font-size: 12px;
  color: ${(props) => props.theme.secondaryTextColor};
`;

const StatValue = styled.span`
  font-size: 14px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: ${(props) => props.theme.textColor};
`;

const MessagesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 300px;
  overflow-y: auto;
  padding: 12px;
  border: 1px solid ${(props) => props.theme.border};
  border-radius: 8px;
  background-color: ${(props) => props.theme.inputBg};
`;

const MessageBubble = styled.div<{ $isUser1: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 70%;
  padding: 8px 12px;
  border-radius: 12px;
  align-self: ${(props) => (props.$isUser1 ? "flex-start" : "flex-end")};
  background-color: ${(props) =>
    props.$isUser1 ? props.theme.colors.primarySoft : props.theme.surfaceRaised};
  border: 1px solid ${(props) => props.theme.border};
`;

const MessageAuthor = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${(props) => props.theme.secondaryTextColor};
`;

const MessageText = styled.span`
  font-size: 14px;
  color: ${(props) => props.theme.textColor};
  overflow-wrap: anywhere;
`;

const MessageTime = styled.span`
  font-size: 11px;
  color: ${(props) => props.theme.mutedTextColor};
  font-variant-numeric: tabular-nums;
`;

const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${secs}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  } else {
    return `${secs}s`;
  }
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString();
};

const Participant: React.FC<{ user?: VideoChatUser }> = ({ user }) => (
  <UserInfo>
    {user?.photoThumbnailUrl ? (
      <UserAvatar src={user.photoThumbnailUrl} alt={user.username} />
    ) : (
      <AvatarFallback>
        <UserIcon width={18} />
      </AvatarFallback>
    )}
    <UserText>
      <UserName>{user?.username || 'Unknown'}</UserName>
      <Mono>ID: {user?._id}</Mono>
    </UserText>
  </UserInfo>
);

const VideoChatHistory = () => {
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set());
  const [limit] = useState(50);
  const [offset] = useState(0);

  const { loading, error, data, refetch } = useQuery(VIDEO_CHAT_HISTORY, {
    variables: { limit, offset },
  });

  const toggleSessionExpansion = (sessionId: string) => {
    setExpandedSessions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sessionId)) {
        newSet.delete(sessionId);
      } else {
        newSet.add(sessionId);
      }
      return newSet;
    });
  };

  const videoChatHistory: VideoChatHistoryData | undefined = data?.videoChatHistory;
  const sessions: VideoChatHistorySession[] = videoChatHistory?.sessions ?? [];

  return (
    <Page
      title="Video chat history"
      badge={
        data && !error && (
          <Badge>{videoChatHistory?.totalCount || 0} sessions</Badge>
        )
      }
      actions={
        <Button
          variant="outlined"
          color="inherit"
          size="small"
          startIcon={<ArrowPathIcon width={15} />}
          onClick={() => refetch()}
        >
          Refresh
        </Button>
      }
    >
      {loading && !data ? (
        <Row justifyContent="center">
          <Loader />
        </Row>
      ) : error ? (
        <ErrorAlert error={error} />
      ) : sessions.length > 0 ? (
        <Panel padding={0}>
          {sessions.map((session) => {
            const isExpanded = expandedSessions.has(session.sessionId);

            return (
              <SessionItem key={session.sessionId}>
                <SessionHeader
                  $expanded={isExpanded}
                  aria-expanded={isExpanded}
                  onClick={() => toggleSessionExpansion(session.sessionId)}
                >
                  <Pair>
                    <Participant user={session.participant1} />
                    <PairIcon>
                      <VideoCameraIcon width={18} />
                    </PairIcon>
                    <Participant user={session.participant2} />
                  </Pair>

                  <HeaderMeta>
                    <MetaCol>
                      <Badge $tone={session.type === 'DIRECT' ? 'primary' : 'neutral'}>
                        {session.type || 'UNKNOWN'}
                      </Badge>
                      <MetaLabel>{formatDate(session.sessionStartedAt)}</MetaLabel>
                    </MetaCol>

                    <MetaCol>
                      <MetaLabel>Duration</MetaLabel>
                      <DurationValue>
                        {formatDuration(session.durationSeconds || 0)}
                      </DurationValue>
                    </MetaCol>

                    <IconButton
                      size="small"
                      aria-label={isExpanded ? 'Collapse session' : 'Expand session'}
                    >
                      {isExpanded ? (
                        <ChevronUpIcon width={18} />
                      ) : (
                        <ChevronDownIcon width={18} />
                      )}
                    </IconButton>
                  </HeaderMeta>
                </SessionHeader>

                <Collapse in={isExpanded}>
                  <SessionDetails>
                    <DetailCol>
                      <SectionTitle>Session details</SectionTitle>

                      <DetailRow>
                        <DetailLabel>Status:</DetailLabel>
                        <Badge $tone={session.status === 'COMPLETED' ? 'green' : 'neutral'}>
                          {session.status || 'UNKNOWN'}
                        </Badge>
                      </DetailRow>

                      <DetailRow>
                        <ClockIcon width={16} />
                        Started: {formatDate(session.sessionStartedAt)}
                      </DetailRow>

                      {session.sessionEndedAt && (
                        <DetailRow>
                          <ClockIcon width={16} />
                          Ended: {formatDate(session.sessionEndedAt)}
                        </DetailRow>
                      )}

                      {session.endReason && (
                        <DetailRow>
                          <DetailLabel>End reason:</DetailLabel>
                          {session.endReason}
                        </DetailRow>
                      )}

                      {session.endedBy && (
                        <DetailRow>
                          <DetailLabel>Ended by:</DetailLabel>
                          {session.endedBy}
                        </DetailRow>
                      )}

                      {(session.participant1JoinedAt || session.participant1LeftAt) && (
                        <>
                          <SectionTitle>
                            {session.participant1?.username} timeline
                          </SectionTitle>
                          {session.participant1JoinedAt && (
                            <DetailRow>
                              <ClockIcon width={16} />
                              Joined: {formatDate(session.participant1JoinedAt)}
                            </DetailRow>
                          )}
                          {session.participant1LeftAt && (
                            <DetailRow>
                              <ClockIcon width={16} />
                              Left: {formatDate(session.participant1LeftAt)}
                            </DetailRow>
                          )}
                        </>
                      )}

                      {(session.participant2JoinedAt || session.participant2LeftAt) && (
                        <>
                          <SectionTitle>
                            {session.participant2?.username} timeline
                          </SectionTitle>
                          {session.participant2JoinedAt && (
                            <DetailRow>
                              <ClockIcon width={16} />
                              Joined: {formatDate(session.participant2JoinedAt)}
                            </DetailRow>
                          )}
                          {session.participant2LeftAt && (
                            <DetailRow>
                              <ClockIcon width={16} />
                              Left: {formatDate(session.participant2LeftAt)}
                            </DetailRow>
                          )}
                        </>
                      )}

                      {session.connectionQuality && (
                        <StatsRow>
                          <SignalIcon width={16} />
                          <Stat>
                            <StatLabel>Latency</StatLabel>
                            <StatValue>
                              {Math.round(session.connectionQuality.averageLatency || 0)}ms
                            </StatValue>
                          </Stat>
                          <Stat>
                            <StatLabel>Packets lost</StatLabel>
                            <StatValue>
                              {session.connectionQuality.packetsLost || 0}
                            </StatValue>
                          </Stat>
                          <Stat>
                            <StatLabel>Reconnections</StatLabel>
                            <StatValue>
                              {session.connectionQuality.reconnections || 0}
                            </StatValue>
                          </Stat>
                        </StatsRow>
                      )}
                    </DetailCol>

                    {session.messages && session.messages.length > 0 && (
                      <DetailCol>
                        <SectionTitle>
                          <ChatBubbleLeftRightIcon width={16} />
                          Chat messages ({session.messages.length})
                        </SectionTitle>

                        <MessagesContainer>
                          {session.messages.map((message: VideoChatMessage) => (
                            <MessageBubble
                              key={message.id}
                              $isUser1={message.fromUserId === session.participant1?._id}
                            >
                              <MessageAuthor>
                                {message.fromUserId === session.participant1?._id
                                  ? session.participant1.username
                                  : session.participant2?.username}
                              </MessageAuthor>
                              <MessageText>{message.text}</MessageText>
                              <MessageTime>{formatDate(message.timestamp)}</MessageTime>
                            </MessageBubble>
                          ))}
                        </MessagesContainer>
                      </DetailCol>
                    )}
                  </SessionDetails>
                </Collapse>
              </SessionItem>
            );
          })}
        </Panel>
      ) : (
        <EmptyState
          Icon={VideoCameraOutlineIcon}
          title="No video chat history available"
        />
      )}
    </Page>
  );
};

export default VideoChatHistory;
